import * as React from 'react'

import { spacing, styled } from '@thg-commerce/gravity-theme'
import { VisuallyHidden } from '@thg-commerce/gravity-elements/VisuallyHidden'

import ErrorMessage from '../../../ErrorMessage'
import { FeatureRatingRectangles } from '../FeatureRatingRectangles'
import { LabelText } from '../../../FormItem/Label'

export interface FeatureRatingProps {
  rating?: string
  ratingType?: string
  onChangeRating?: (number) => void
  screenReaderOnlyText: string
  bindref?: any
  required?: boolean
  label: string
  error?: string | boolean
  measurementLabels?: string[]
  className?: string
}

const StyledLabelText = styled(LabelText)`
  margin-bottom: ${spacing(1)};
`

export const FeatureRating = (props: FeatureRatingProps) => {
  React.useImperativeHandle(props.bindref, () => ({
    ...(props as FeatureRatingProps & HTMLButtonElement),
    validate: (): boolean | undefined => {
      const valid = props.required ? featureRatingState !== '' : true

      if (!valid && barsEl !== null && barsEl.current !== null) {
        setError(`${props.label} is a required field`)
      }
      return valid
    },
    value: featureRatingState,
  }))

  const requiredProps = {
    required: props.required,
    'aria-required': props.required,
  }

  const barsEl: React.RefObject<HTMLInputElement> =
    props.bindref || React.useRef<HTMLInputElement>(null)

  const [featureRatingState, setFeatureRatingState] = React.useState(
    barsEl !== null && barsEl.current !== null
      ? barsEl.current.value
      : props.rating || '',
  )

  const [error, setError] = React.useState<string | boolean>(false)

  React.useEffect(() => {
    if (props.error) {
      setError(props.error)
    }
  }, [props.error])

  return (
    <div
      className={props.className}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
      }}
    >
      <StyledLabelText required={props.required}>{props.label}</StyledLabelText>
      <input
        type="hidden"
        value={featureRatingState}
        ref={barsEl}
        {...requiredProps}
        onChange={(_: React.ChangeEvent<HTMLInputElement>) => {
          if (barsEl !== null && barsEl.current !== null) {
            setFeatureRatingState(barsEl.current.value)
          }
        }}
      />
      <VisuallyHidden text={props.screenReaderOnlyText} />

      <FeatureRatingRectangles
        featureRatingType={props.ratingType}
        measurementLabels={props.measurementLabels}
        setFeatureRatingState={setFeatureRatingState}
        setError={setError}
        featureRatingState={parseInt(featureRatingState, 10)}
      />

      {error && <ErrorMessage id={`rating-stars-error`} error={error} />}
    </div>
  )
}
